import React, { lazy, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Link as LinkLi } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { pageUrls, ROLES } from 'globalconfig/constants/keys';
import { mediaUrls } from 'globalconfig/constants/mediaUrls';
import { hasRestrictedRole, hasValue, isdvirOnly, isInsuracePortalUser } from 'globalutils/fmUtils';
import { getConfig, getELDStatusIcon } from './config';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window?.$environment?.CURRENT_LAYOUT;
const SvgIcon = lazy(() => import(`globalcomponents/SvgIcon/${layout}`));
const Icon = lazy(() => import(`globalcomponents/UI/Icon/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const Table = lazy(() => import(`globalcomponents/UI/Table/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Modal = require(`globalcomponents/UI/Modal/${layout}`).default;

export default function UserList({
  request,
  users,
  changeRequest,
  showAllUsers,
  showCompany,
  isMechanicUser,
  tableData,
  apiStatus,
  fetchUsers,
  viewOnly,
  handleResetPassword,
  callback,
  handleDeleteAlert,
  handleRestoreAlert,
  pagination,
  setPagination,
  setisDocClicked,
  clickedRow,
}) {
  const SERVICE_PROVIDER = window?.$environment?.SERVICE_PROVIDER;
  const i18n = useTranslation();
  const [modal, setModal] = useState(null);
  const insuracePortalUserRole = isInsuracePortalUser();
  const isDvirOnlyFlagEnabled = isdvirOnly();

  // ✅ Memoized rows to avoid recalculating on every render
  const generateRows = useMemo(() => {
    return tableData.map((item) => ({
      ...item,
      eld: <div className={getELDStatusIcon(item.eldState)} />,
    }));
  }, [tableData]);

  const hasUserListApiStatus = hasValue(apiStatus.status, 'api', fetchUsers);
  const information = hasUserListApiStatus?.errorMessage ? (
    <Message type="error" message={i18n.t(hasUserListApiStatus.errorMessage)} />
  ) : (
    i18n.t('common.tableEmptyMessage')
  );

  // ✅ Memoized columns
  const columns = useMemo(() => {
    const cols = [
      ...getConfig({
        i18n,
        insuracePortalUserRole,
        showAllUsers,
        showCompany,
        isMechanicUser,
      }).tableColumns,
    ];

    if (isDvirOnlyFlagEnabled) {
      if (hasRestrictedRole([ROLES.SERVICE_MANAGER])) {
        return cols.filter((_, index) => index !== 5);
      }
      return cols.slice(0, -2);
    }

    return cols;
  }, [insuracePortalUserRole, showAllUsers, showCompany, isMechanicUser, i18n, isDvirOnlyFlagEnabled]);

  const getModalContent = () => {
    if (!modal) return null;

    const isActive = modal.status === 'Active';
    const actionLabel = isActive ? i18n.t('vehicles.deactivateUser') : i18n.t('vehicles.activateIt');
    const actionHandler = isActive ? handleDeleteAlert : handleRestoreAlert;
    const actionId = isActive ? 'deleteAlert' : 'restoreAlert';
    const popupMessage = isActive ? i18n.t('common.popupMessage') : i18n.t('common.activateMessage');

    return {
      content: () => (
        <div className="deleteParent">
          <div className="imageSection">
            <SvgIcon value={mediaUrls.confirmIcon} alt={i18n.t('common.sureConfirm')} />
          </div>
          <h2>{i18n.t('common.sureConfirm')}</h2>
          <p>{popupMessage}</p>
          <div className="deletePopupBtn">
            <div className="cancelBtn">
              <Button label={i18n.t('Cancel')} type="reset" onClick={() => setModal(null)} />
            </div>
            <div className="deletebtn">
              <Button
                label={actionLabel}
                onClick={() => {
                  actionHandler({
                    id: actionId,
                    data: { id: modal.id },
                    callback,
                    filters: isMechanicUser && isActive ? { userRole: 'MECHANIC' } : null,
                  });
                  setModal(null);
                }}
              />
            </div>
          </div>
        </div>
      ),
    };
  };

  const getActionIcons = (row) => {
    const isDriverOrDefault =
      (row.role === 'Driver' &&
        checkServiceProvider('isDriverOrDefault', SERVICE_PROVIDER, false) &&
        hasRestrictedRole(['FLMANAGER'])) ||
      (row.role.includes('Default') && !hasRestrictedRole(['OWNER']));

    if (isDriverOrDefault) return null;

    return (
      <Icon
        src={row.status === 'Active' ? mediaUrls?.deleteIcon : mediaUrls?.undelete}
        is
        tooltip={row.status === 'Active' ? i18n.t('common.deactivate') : i18n.t('common.activate')}
        onClick={() => setModal({ ...row })}
        className={row.status === 'Active' ? '' : 'undelete'}
      />
    );
  };

  return (
    <>
      <Table
        type={insuracePortalUserRole ? 'client' : 'server'}
        defaultSortBy={{ sortBy: 'full_name', order: 'asc' }}
        title=""
        baseRequest={request}
        totalRecords={users.count}
        changeRequest={changeRequest}
        columns={columns}
        data={generateRows}
        emptyMessage={information}
        itemsPerPage={10}
        actions={[
          {
            icon: (row) => (
              <LinkLi
                component={Link}
                to={
                  viewOnly
                    ? pageUrls.viewUser.replace(':id', row.id)
                    : isMechanicUser
                    ? pageUrls.editMechanic.replace(':id', row.id)
                    : pageUrls.editUser.replace(':id', row.id)
                }
              >
                {viewOnly ? (
                  <VisibilityIcon className="historyIcon" />
                ) : (
                  <Icon src={mediaUrls?.editSvg} tooltip={i18n.t('common.edit')} />
                )}
              </LinkLi>
            ),
          },
          ...(hasRestrictedRole(['FLMANAGER'])
            ? [
                {
                  icon: (row) => (
                    <Icon
                      onClick={() => {
                        handleResetPassword({
                          id: 'resetPassword',
                          data: { id: row.id },
                          callback,
                        });
                      }}
                      src={mediaUrls?.refreshIcon}
                      tooltip={i18n.t('common.resetPassword')}
                    />
                  ),
                },
              ]
            : []),
          ...(!viewOnly && checkServiceProvider('getActionIcons', SERVICE_PROVIDER) ? [{ icon: getActionIcons }] : []),
        ]}
        clickedRow={(row, id) => clickedRow(id)}
        pageinationReset={pagination}
        pagenationClicked={(e) => {
          setPagination(!e);
          setisDocClicked(false);
        }}
        i18n={i18n}
        newUI={checkServiceProvider('newUI', SERVICE_PROVIDER, false)}
      />
      {modal && <Modal open={true} setOpen={setModal} data={getModalContent()} deletePopup />}
    </>
  );
}
